<template>
  <div id="app">
    <header v-if="authStore.isAuthenticated">
      <nav class="navbar">
        <div class="container">
          <div class="navbar-brand">
            <router-link to="/" class="brand-title">Budget Buddy</router-link>
          </div>
          <div class="navbar-menu">
            <router-link to="/dashboard" class="nav-link">Dashboard</router-link>
            <router-link to="/review" class="nav-link nav-link-review">Review</router-link>
            <router-link to="/transactions" class="nav-link">Transactions</router-link>
            <router-link to="/monarch/transactions" class="nav-link">Monarch Txns</router-link>
            <router-link to="/categories" class="nav-link">Categories</router-link>
            <router-link to="/settings" class="nav-link">Settings</router-link>
            <div class="user-menu">
              <img v-if="authStore.user?.picture" :src="authStore.user.picture" class="user-avatar" />
              <span>{{ authStore.user?.name }}</span>
              <button @click="handleLogout" class="btn-logout">Logout</button>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <main class="main-content">
      <router-view />
    </main>

    <!-- Monarch Re-auth Popup -->
    <div v-if="showReauthPopup" class="modal-overlay" @click.self="dismissReauthPopup">
      <div class="reauth-modal">
        <div class="reauth-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#f39c12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <h3 class="reauth-title">Monarch Money Disconnected</h3>
        <p class="reauth-body">
          Your Monarch Money session has expired or your credentials are no longer valid.
          Transaction syncing is paused until you reconnect.
        </p>
        <div class="reauth-actions">
          <button class="btn-reauth-dismiss" @click="dismissReauthPopup">Dismiss</button>
          <router-link to="/settings" class="btn-reauth-settings" @click="dismissReauthPopup">Go to Settings</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from './store/auth'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()
const showReauthPopup = ref(false)

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

const onMonarchAuthRequired = () => {
  authStore.setMonarchNeedsReauth()
  showReauthPopup.value = true
}

const dismissReauthPopup = () => {
  showReauthPopup.value = false
}

watch(() => authStore.monarchNeedsReauth, (val) => {
  if (val) {
    showReauthPopup.value = true
  }
})

onMounted(() => {
  window.addEventListener('monarch-auth-required', onMonarchAuthRequired)
})

onUnmounted(() => {
  window.removeEventListener('monarch-auth-required', onMonarchAuthRequired)
})
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  background-color: #f5f5f5;
}

#app {
  min-height: 100vh;
}

.navbar {
  background-color: #2c3e50;
  color: white;
  padding: 1rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.navbar-brand .brand-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: white;
  text-decoration: none;
}

.navbar-menu {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.nav-link {
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.nav-link:hover,
.nav-link.router-link-active {
  background-color: rgba(255, 255, 255, 0.1);
}

.nav-link-review {
  background-color: #e67e22;
  font-weight: 600;
}

.nav-link-review:hover,
.nav-link-review.router-link-active {
  background-color: #d35400;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: 1rem;
  padding-left: 1rem;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
}

.btn-logout {
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
}

.btn-logout:hover {
  background-color: #c0392b;
}

.main-content {
  max-width: 1200px;
  margin: 2rem auto;
  padding: 0 1rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.reauth-modal {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.reauth-icon {
  margin-bottom: 1rem;
}

.reauth-title {
  color: #2c3e50;
  margin: 0 0 0.75rem;
  font-size: 1.25rem;
}

.reauth-body {
  color: #7f8c8d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.reauth-actions {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.btn-reauth-dismiss {
  background: none;
  border: 1px solid #bdc3c7;
  color: #7f8c8d;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
}

.btn-reauth-dismiss:hover {
  border-color: #7f8c8d;
  color: #2c3e50;
}

.btn-reauth-settings {
  background-color: #f39c12;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  text-decoration: none;
  cursor: pointer;
}

.btn-reauth-settings:hover {
  background-color: #e67e22;
}
</style>
